import { useMemo, useState } from 'react'
import { Info, Shield } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import {
  eligibilityAcknowledgements,
  feeAcknowledgements,
  htmlWithoutLastList,
  splitWelcomeHtml,
  WELCOME_ACCEPTANCE_CTA,
  type InviteAckItem,
} from '../utils/inviteAcceptanceContent'
import { InviteOnboardingLayout } from './InviteOnboardingStepper'
import {
  inviteAckContentClass,
  inviteAckRowHoverClass,
  inviteCheckboxClass,
  invitePrimaryButtonClass,
  invitePrimaryButtonCompactClass,
  inviteSecondaryButtonClass,
  inviteSectionIconClass,
  inviteWelcomeContentClass,
} from './inviteOnboardingUi'

type AcceptancePhase = 'welcome' | 'acknowledgements'

type InvitationAcceptanceDialogProps = {
  welcomeHtml: string
  feesHtml: string
  eligibilityHtml: string
  saving: boolean
  error?: string
  /** Invite lookup still running — welcome stays up and the start button stays off. */
  loading?: boolean
  /** Invite lookup failed — keep the welcome step and block acknowledgement. */
  startDisabled?: boolean
  statusMessage?: string
  onComplete: (payload: { fees: Record<string, boolean>; eligibility: Record<string, boolean> }) => void
}

const contentClass = inviteAckContentClass

function withoutFirstHeading(html: string) {
  return html.replace(/^\s*<h[1-3][^>]*>[\s\S]*?<\/h[1-3]>\s*/i, '')
}

function hasVisibleHtml(html: string) {
  return Boolean(html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').trim())
}

function AckList({
  items,
  checked,
  onToggle,
}: {
  items: InviteAckItem[]
  checked: Record<string, boolean>
  onToggle: (key: string) => void
}) {
  return (
    <div className="mt-4 space-y-3">
      {items.map((item) => (
        <label
          key={item.key}
          className={cn(
            'flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm transition',
            inviteAckRowHoverClass,
          )}
        >
          <input
            type="checkbox"
            className={inviteCheckboxClass}
            checked={Boolean(checked[item.key])}
            onChange={() => onToggle(item.key)}
          />
          <span className="text-sm leading-relaxed text-slate-800">
            {item.label}
            {item.required ? (
              <span className="text-red-600" aria-label="required">
                {' '}
                *
              </span>
            ) : null}
          </span>
        </label>
      ))}
    </div>
  )
}

export function InvitationAcceptanceDialog({
  welcomeHtml,
  feesHtml,
  eligibilityHtml,
  saving,
  error,
  loading = false,
  startDisabled = false,
  statusMessage,
  onComplete,
}: InvitationAcceptanceDialogProps) {
  const [phase, setPhase] = useState<AcceptancePhase>('welcome')
  const [feesChecked, setFeesChecked] = useState<Record<string, boolean>>({})
  const [eligibilityChecked, setEligibilityChecked] = useState<Record<string, boolean>>({})

  const welcomeBody = useMemo(() => splitWelcomeHtml(welcomeHtml), [welcomeHtml])
  const fees = useMemo(
    () => feeAcknowledgements(feesHtml).filter((item) => item.label.trim()),
    [feesHtml],
  )
  const eligibility = useMemo(
    () => eligibilityAcknowledgements(eligibilityHtml).filter((item) => item.label.trim()),
    [eligibilityHtml],
  )
  const feesBody = useMemo(() => withoutFirstHeading(htmlWithoutLastList(feesHtml)), [feesHtml])
  const eligibilityBody = useMemo(
    () => withoutFirstHeading(htmlWithoutLastList(eligibilityHtml)),
    [eligibilityHtml],
  )

  const feesComplete = fees.filter((item) => item.required).every((item) => feesChecked[item.key])
  const eligibilityComplete = eligibility
    .filter((item) => item.required)
    .every((item) => eligibilityChecked[item.key])
  const acknowledgementsComplete = feesComplete && eligibilityComplete

  if (phase === 'welcome') {
    return (
      <InviteOnboardingLayout currentStep="welcome">
        <div
          className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white px-6 py-10 shadow-sm sm:px-10"
          data-testid="invitation-acceptance-dialog"
        >
          {loading ? (
            <p className="text-sm text-slate-600" data-testid="invitation-acceptance-loading">
              Loading invitation...
            </p>
          ) : (
            <div className={inviteWelcomeContentClass} dangerouslySetInnerHTML={{ __html: welcomeBody }} />
          )}
          {statusMessage ? (
            <p className="mt-4 text-sm text-red-600" data-testid="invitation-acceptance-status">
              {statusMessage}
            </p>
          ) : null}
          <button
            type="button"
            className={cn('mt-8', invitePrimaryButtonClass)}
            onClick={() => setPhase('acknowledgements')}
            disabled={loading || startDisabled || saving}
            data-testid="invitation-acceptance-start"
          >
            {WELCOME_ACCEPTANCE_CTA}
            <span aria-hidden className="ml-2">
              →
            </span>
          </button>
        </div>
      </InviteOnboardingLayout>
    )
  }

  return (
    <InviteOnboardingLayout currentStep="acknowledgements">
      <div
        className="mx-auto max-w-3xl space-y-6"
        data-testid="invitation-acceptance-dialog"
      >
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <Info className={inviteSectionIconClass} aria-hidden />
            Fees and Obligations
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Please review the following information about the Liberty Rent program and click each
            box to indicate your acceptance.
          </p>
          {hasVisibleHtml(feesBody) ? (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className={contentClass} dangerouslySetInnerHTML={{ __html: feesBody }} />
            </div>
          ) : null}
          <AckList
            items={fees}
            checked={feesChecked}
            onToggle={(key) => setFeesChecked((current) => ({ ...current, [key]: !current[key] }))}
          />
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <Shield className={inviteSectionIconClass} aria-hidden />
            Program Eligibility
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Please review the eligibility requirements for the Liberty Rent program and click each
            box to indicate your agreement.
          </p>
          {hasVisibleHtml(eligibilityBody) ? (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className={contentClass} dangerouslySetInnerHTML={{ __html: eligibilityBody }} />
            </div>
          ) : null}
          <AckList
            items={eligibility}
            checked={eligibilityChecked}
            onToggle={(key) =>
              setEligibilityChecked((current) => ({ ...current, [key]: !current[key] }))
            }
          />
        </section>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <div className="flex flex-col-reverse items-stretch gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <button
            type="button"
            className={inviteSecondaryButtonClass}
            onClick={() => setPhase('welcome')}
            disabled={saving}
          >
            Back
          </button>
          <div className="flex flex-1 flex-col items-center gap-2 sm:flex-row sm:justify-end">
            {!acknowledgementsComplete ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 sm:mr-3">
                <span aria-hidden>!</span>
                Please acknowledge all required items to continue.
              </span>
            ) : null}
            <button
              type="button"
              className={invitePrimaryButtonCompactClass}
              disabled={!acknowledgementsComplete || saving}
              onClick={() =>
                onComplete({
                  fees: Object.fromEntries(fees.map((item) => [item.key, Boolean(feesChecked[item.key])])),
                  eligibility: Object.fromEntries(
                    eligibility.map((item) => [item.key, Boolean(eligibilityChecked[item.key])]),
                  ),
                })
              }
              data-testid="invitation-acceptance-complete"
            >
              {saving ? 'Saving...' : 'Complete Acceptance'}
              {!saving ? (
                <span aria-hidden className="ml-2">
                  →
                </span>
              ) : null}
            </button>
          </div>
        </div>
      </div>
    </InviteOnboardingLayout>
  )
}
